import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertBanner,
  Breadcrumb,
  Button,
  Card,
  EmptyState,
  FileUpload,
  Modal,
  QrCode,
  Select,
  Skeleton,
  StatusBadge,
  Tabs,
  Tag,
  Timeline,
  useLocale,
  useToast,
  type FileUploadState,
  type TimelineEvent,
} from '@ministry/ui/internal';

import { archiveAttachment, attachmentPreviewKind, getFile, getFileQr, listFileAttachments, uploadAttachment } from '../lib/files.js';
import { listMovements } from '../lib/workflow.js';
import { ApiError, API_BASE_URL } from '../lib/api-client.js';
import { fileStageEntry, OVERDUE_FLAG, confidentialityLabel, CONFIDENTIALITY_LEVELS, isAboveInternalClearance, movementActionEntry } from '../lib/file-lifecycle.js';
import { trackingQrUrl } from '../lib/qr.js';
import { useSession } from '../lib/session.js';
import { ROUTES } from '../lib/nav.js';
import { useT } from '../i18n/strings.js';
import { MovementActionModal, type MovementActionKind } from '../components/MovementActionModal.js';
import { ClassifyModal, DartaChalaniModal, EditFileModal, ReassignModal, ReopenModal } from '../components/FileActionModals.js';
import { AttachmentPreviewModal } from '../components/AttachmentPreviewModal.js';
import { formatDueDisplay, formatRegisteredDisplay } from '../lib/dates.js';

/** SCREEN_SPECS.md I8. */
export function FileDetailPage(): React.JSX.Element {
  const { fileId } = useParams<{ fileId: string }>();
  const t = useT();
  const { locale } = useLocale();
  const { push } = useToast();
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const [activeAction, setActiveAction] = React.useState<MovementActionKind | null>(null);
  const [cursors, setCursors] = React.useState<(string | undefined)[]>([undefined]);
  type FileModalKind = 'edit' | 'darta' | 'classify' | 'reopen' | 'reassign' | 'qr';
  const [activeModal, setActiveModal] = React.useState<FileModalKind | null>(null);
  const [uploadState, setUploadState] = React.useState<FileUploadState>({ status: 'idle' });
  const [uploadConfidentiality, setUploadConfidentiality] = React.useState<string>('');
  const [archivingId, setArchivingId] = React.useState<string | null>(null);
  const [previewAttachmentId, setPreviewAttachmentId] = React.useState<string | null>(null);

  const fileQuery = useQuery({
    queryKey: ['file', fileId],
    queryFn: ({ signal }) => getFile(fileId as string, signal),
    enabled: Boolean(fileId),
  });

  const movementsQuery = useQuery({
    queryKey: ['file-movements', fileId, cursors],
    queryFn: async ({ signal }) => {
      const pages = await Promise.all(cursors.map((cursor) => listMovements(fileId as string, { cursor }, signal)));
      return pages;
    },
    enabled: Boolean(fileId),
  });

  const attachmentsQuery = useQuery({
    queryKey: ['file-attachments', fileId],
    queryFn: ({ signal }) => listFileAttachments(fileId as string, signal),
    enabled: Boolean(fileId),
  });

  const invalidateFile = () => {
    void queryClient.invalidateQueries({ queryKey: ['file', fileId] });
    void queryClient.invalidateQueries({ queryKey: ['file-movements', fileId] });
  };
  const invalidateAttachments = () => {
    void queryClient.invalidateQueries({ queryKey: ['file-attachments', fileId] });
  };
  /**
   * Named defect: every one of these actions used to close its modal and refetch
   * silently — a changed status badge is a real confirmation, but a `Reassign`/`Link
   * darta-chalani`/`Reopen` doesn't necessarily change anything visible in the tab the
   * user is currently looking at, so nothing told them the write actually happened.
   * `useToast` is the one shared mechanism now (§6 #22); this stays a plain function
   * rather than baking the message into each modal so every action confirms through
   * the exact same success path FileDetailPage already uses for `invalidateFile`.
   */
  const notifySuccess = (key: string) => push({ variant: 'success', title: t(key) });

  /**
   * §10 documents an asynchronous scanner; THIS deployment scans synchronously inside the
   * transaction (`file-service.ts`'s own comment), so a `201` always means `attached: true`
   * today. `ATTACHMENT_NOT_CLEAN` is `retryable: true` (§4.1) — rendered as its own
   * `FileUpload` error kind (`errorKind: 'malware'`), never folded into a generic failure,
   * because a pending/failed scan is a real, honest state, not an error the citizen or
   * officer did anything wrong to cause.
   */
  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadAttachment(fileId as string, file, uploadConfidentiality || undefined),
    onMutate: (file) => {
      setUploadState({ status: 'uploading', fileName: file.name, progressPercent: 50 });
    },
    onSuccess: (result) => {
      setUploadState({ status: 'success', fileName: result.originalFilename });
      invalidateAttachments();
    },
    onError: (error, file) => {
      const errorKind =
        error instanceof ApiError && error.code === 'ATTACHMENT_NOT_CLEAN'
          ? 'malware'
          : error instanceof ApiError && error.code === 'UNSUPPORTED_MEDIA_TYPE'
            ? 'type'
            : error instanceof ApiError && error.code === 'PAYLOAD_TOO_LARGE'
              ? 'size'
              : 'generic';
      setUploadState({ status: 'error', fileName: file.name, errorKind });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (attachmentId: string) => archiveAttachment(attachmentId),
    onSuccess: () => {
      setArchivingId(null);
      invalidateAttachments();
    },
  });

  const qrQuery = useQuery({
    queryKey: ['file-qr', fileId],
    queryFn: ({ signal }) => getFileQr(fileId as string, signal),
    enabled: activeModal === 'qr' && Boolean(fileId),
  });

  if (!fileId) return <></>;

  if (fileQuery.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <span role="status" aria-live="polite" className="sr-only">
          {t('fileDetail.loading')}
        </span>
        <Skeleton shape="block" className="h-8 w-64" />
        <Skeleton shape="list-row" count={4} />
      </div>
    );
  }

  if (fileQuery.isError) {
    const error = fileQuery.error;
    let title = t('fileDetail.error.generic.title');
    let body = t('fileDetail.error.generic.body');
    if (error instanceof ApiError) {
      // API_CONTRACT.md §7: four DIFFERENT 403s, never collapsed into one message.
      if (error.code === 'NOT_FOUND') {
        title = t('fileDetail.error.notFound.title');
        body = t('fileDetail.error.notFound.body');
      } else if (error.code === 'FORBIDDEN_ROLE') {
        title = t('fileDetail.error.forbiddenRole.title');
        body = t('fileDetail.error.forbiddenRole.body');
      } else if (error.code === 'FORBIDDEN_SCOPE') {
        title = t('fileDetail.error.forbiddenScope.title');
        body = t('fileDetail.error.forbiddenScope.body');
      } else if (error.code === 'FORBIDDEN_OWNERSHIP') {
        title = t('fileDetail.error.forbiddenOwnership.title');
        body = t('fileDetail.error.forbiddenOwnership.body');
      } else if (error.code === 'FORBIDDEN_CLEARANCE') {
        title = t('fileDetail.error.forbiddenClearance.title');
        body = t('fileDetail.error.forbiddenClearance.body');
      }
    }
    return (
      <div className="flex flex-col gap-4">
        <EmptyState
          title={title}
          body={body}
          action={
            error instanceof ApiError && (error.code === 'NOT_FOUND' || error.code.startsWith('FORBIDDEN')) ? (
              <Link to={ROUTES.files} className="text-sm font-semibold text-text-link underline">
                {t('fileDetail.back')}
              </Link>
            ) : (
              <Button size="sm" variant="secondary" onClick={() => fileQuery.refetch()}>
                {t('common.retry')}
              </Button>
            )
          }
        />
      </div>
    );
  }

  const file = fileQuery.data;
  if (!file) return <></>;

  const isHolder = session ? file.currentHolderUserId === session.user.id : false;
  const canAct = isHolder && (session?.permissions.includes('file.move') ?? false);
  const isAboveInternal = isAboveInternalClearance(file.confidentialityLevel);
  const isAuditor = session?.roles.some((role) => role.roleCode === 'auditor') ?? false;

  const movementRows = (movementsQuery.data ?? []).flatMap((page) => page.items);
  const lastPage = movementsQuery.data?.at(-1);
  const hasMoreMovements = lastPage?.page.hasMore ?? false;

  const timelineEvents: TimelineEvent[] = movementRows.map((movement) => ({
    id: movement.id,
    stage: movementActionEntry(movement.action),
    when: `${movement.occurredDateBs.formatted} · ${new Date(movement.occurredAt).toLocaleTimeString(locale === 'ne' ? 'ne-NP' : 'en-US')}`,
    fromOffice: locale === 'ne' ? (movement.from.orgUnitNameNe ?? undefined) : (movement.from.orgUnitNameEn ?? undefined),
    toOffice: (locale === 'ne' ? movement.to.orgUnitNameNe : movement.to.orgUnitNameEn) ?? undefined,
    actorName: movement.actor.userName ?? undefined,
    remarks: movement.remarks ?? undefined,
  }));

  const overview = (
    <div className="flex flex-col gap-4">
      <Card className="flex flex-col gap-2">
        <h2 className="text-lg font-semibold text-text-heading">{t('fileDetail.applicantTitle')}</h2>
        {file.applicant ? (
          <dl className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
            <div>
              <dt className="text-sm text-text-muted">{t('fileDetail.applicantPhone')}</dt>
              <dd className="text-text-primary">{file.applicant.phone}</dd>
            </div>
            <div>
              <dt className="text-sm text-text-muted">{t('fileDetail.applicantEmail')}</dt>
              <dd className="text-text-primary">{file.applicant.email ?? t('fileDetail.notProvided')}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-sm text-text-muted">{t('fileDetail.applicantAddress')}</dt>
              <dd className="text-text-primary">{file.applicant.addressText ?? t('fileDetail.notProvided')}</dd>
            </div>
          </dl>
        ) : (
          <p className="text-text-muted">{t('fileDetail.applicantNone')}</p>
        )}
      </Card>

      {canAct ? (
        <Card className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold text-text-heading">{t('fileDetail.actionsTitle')}</h2>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setActiveAction('forward')}>{t('fileDetail.actionForward')}</Button>
            <Button variant="secondary" onClick={() => setActiveAction('approve')}>
              {t('fileDetail.actionApprove')}
            </Button>
            <Button variant="destructive" onClick={() => setActiveAction('reject')}>
              {t('fileDetail.actionReject')}
            </Button>
            <Button variant="ghost" onClick={() => setActiveAction('comment')}>
              {t('fileDetail.actionComment')}
            </Button>
          </div>
        </Card>
      ) : session?.permissions.includes('file.move') ? (
        <AlertBanner variant="info" title={t('fileDetail.notHolder')} />
      ) : null}

      {/*
        A second, permission-gated action row for the record-level writes this build adds
        (edit / classify / QR / reopen / reassign) — deliberately separate from the
        holder-only workflow-action card above, since these do NOT require being the
        current holder (§13.2/§13.3's Own/Clr columns differ per endpoint) and gate on
        their OWN permission code, never on `canAct`.
      */}
      {file.archivedAt === null &&
      (session?.permissions.includes('file.update') ||
        session?.permissions.includes('file.classify') ||
        session?.permissions.includes('file.read') ||
        (session?.permissions.includes('movement.reopen') && file.statusCode === 'rejected') ||
        session?.permissions.includes('movement.reassign')) ? (
        <Card className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold text-text-heading">{t('fileDetail.recordActionsTitle')}</h2>
          <div className="flex flex-wrap gap-2">
            {session?.permissions.includes('file.update') ? (
              <Button variant="secondary" size="sm" onClick={() => setActiveModal('edit')}>
                {t('fileDetail.edit.action')}
              </Button>
            ) : null}
            {session?.permissions.includes('file.classify') ? (
              <Button variant="secondary" size="sm" onClick={() => setActiveModal('classify')}>
                {t('fileDetail.classify.action')}
              </Button>
            ) : null}
            <Button variant="secondary" size="sm" onClick={() => setActiveModal('qr')}>
              {t('fileDetail.qr.action')}
            </Button>
            {session?.permissions.includes('movement.reopen') && file.statusCode === 'rejected' ? (
              <Button variant="secondary" size="sm" onClick={() => setActiveModal('reopen')}>
                {t('fileDetail.reopen.action')}
              </Button>
            ) : null}
            {session?.permissions.includes('movement.reassign') ? (
              <Button variant="secondary" size="sm" onClick={() => setActiveModal('reassign')}>
                {t('fileDetail.reassign.action')}
              </Button>
            ) : null}
          </div>
        </Card>
      ) : null}

      {isAuditor && isAboveInternal ? <AlertBanner variant="info" title={t('fileDetail.auditNotice')} /> : null}
    </div>
  );

  const history = (
    <div className="flex flex-col gap-3">
      <Timeline
        events={timelineEvents}
        loading={movementsQuery.isLoading}
        error={movementsQuery.isError}
        onRetry={() => movementsQuery.refetch()}
      />
      {hasMoreMovements ? (
        <Button
          variant="secondary"
          size="sm"
          className="self-start"
          loading={movementsQuery.isFetching}
          onClick={() => setCursors((prev) => [...prev, lastPage?.page.nextCursor ?? undefined])}
        >
          {t('common.viewAll')}
        </Button>
      ) : null}
    </div>
  );

  const attachments = (
    <div className="flex flex-col gap-4">
      {session?.permissions.includes('attachment.write') && file.archivedAt === null ? (
        <Card className="flex flex-col gap-3">
          <h2 className="text-sm font-semibold text-text-primary">{t('fileDetail.upload.action')}</h2>
          <Select
            id="attachment-confidentiality"
            label={t('fileDetail.upload.confidentiality')}
            value={uploadConfidentiality || undefined}
            onValueChange={setUploadConfidentiality}
            options={CONFIDENTIALITY_LEVELS.map((lvl) => ({ value: lvl.code, label: locale === 'ne' ? lvl.labelNe : lvl.labelEn }))}
          />
          <FileUpload
            id="attachment-upload"
            label={t('fileDetail.upload.action')}
            state={uploadState}
            disabled={uploadMutation.isPending}
            onFileSelected={(file) => uploadMutation.mutate(file)}
          />
        </Card>
      ) : null}

      {attachmentsQuery.isLoading ? (
        <Skeleton shape="list-row" count={3} />
      ) : attachmentsQuery.isError ? (
        <EmptyState
          title={t('fileDetail.attachmentsError.title')}
          body={t('common.somethingWentWrong')}
          action={
            <Button size="sm" variant="secondary" onClick={() => attachmentsQuery.refetch()}>
              {t('common.retry')}
            </Button>
          }
        />
      ) : (attachmentsQuery.data ?? []).length === 0 ? (
        <EmptyState title={t('fileDetail.attachmentsEmpty.title')} body={t('fileDetail.attachmentsEmpty.body')} />
      ) : (
        <ul className="flex flex-col gap-2">
          {(attachmentsQuery.data ?? []).map((attachment) => (
            <li key={attachment.id} className="flex items-center justify-between gap-3 rounded-md border border-border-decorative p-3">
              <div className="min-w-0">
                <p className="truncate font-medium text-text-primary">{attachment.originalFilename}</p>
                <p className="text-xs text-text-muted">
                  {(attachment.byteSize / 1024).toFixed(0)} KB · {confidentialityLabel(attachment.confidentialityLevel, locale)}
                  {attachment.archivedAt ? ` · ${t('fileDetail.archive.action')}` : ''}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                {!attachment.attached ? (
                  <span title={t('fileDetail.attachmentPendingScanRetryable')}>
                    <Tag>{t('fileDetail.attachmentPendingScan')}</Tag>
                  </span>
                ) : session?.permissions.includes('attachment.read') === false ? null : (
                  <>
                    {/*
                      "not view option of document" — an in-app Preview action BESIDE
                      Download, never instead of it (coordinator's brief). Gated on the
                      SAME allowlist `AttachmentPreviewModal` itself re-checks (PDF/raster
                      image, clean scan) — an attachment outside it (Word doc, zip, an
                      unscanned upload) shows Download only, never a Preview control that
                      would open to nothing but the "not supported" state.
                    */}
                    {attachmentPreviewKind(attachment) !== null ? (
                      <button
                        type="button"
                        className="text-sm font-semibold text-text-link underline"
                        onClick={() => setPreviewAttachmentId(attachment.id)}
                      >
                        {t('fileDetail.preview.action')}
                      </button>
                    ) : null}
                    {isAboveInternalClearance(attachment.confidentialityLevel) ? (
                      <a
                        className="text-sm font-semibold text-text-link underline"
                        href={`${API_BASE_URL}/attachments/${attachment.id}/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={t('fileDetail.attachmentAboveInternal')}
                      >
                        🔒 {t('fileDetail.attachmentAboveInternal')}
                      </a>
                    ) : (
                      <a
                        className="text-sm font-semibold text-text-link underline"
                        href={`${API_BASE_URL}/attachments/${attachment.id}/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('fileDetail.attachmentDownload')}
                      </a>
                    )}
                  </>
                )}
                {session?.permissions.includes('attachment.archive') && attachment.archivedAt === null ? (
                  <Button size="sm" variant="ghost" onClick={() => setArchivingId(attachment.id)}>
                    {t('fileDetail.archive.action')}
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const darta = (
    <Card className="flex flex-col gap-3">
      {file.dartaChalani.dartaNumber || file.dartaChalani.chalaniNumber || file.dartaChalani.registerDate ? (
        <dl className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
          <div>
            <dt className="text-sm text-text-muted">{t('fileDetail.dartaNumber')}</dt>
            <dd className="text-text-primary">{file.dartaChalani.dartaNumber ?? t('fileDetail.notProvided')}</dd>
          </div>
          <div>
            <dt className="text-sm text-text-muted">{t('fileDetail.chalaniNumber')}</dt>
            <dd className="text-text-primary">{file.dartaChalani.chalaniNumber ?? t('fileDetail.notProvided')}</dd>
          </div>
          <div>
            <dt className="text-sm text-text-muted">{t('fileDetail.dartaRegisterDate')}</dt>
            <dd className="text-text-primary">{file.dartaChalani.registerDate ?? t('fileDetail.notProvided')}</dd>
          </div>
        </dl>
      ) : (
        <p className="text-text-muted">{t('fileDetail.dartaNotLinked')}</p>
      )}
      {/* A-004's "linked later" — this was display-only until this build; §13.2 gates the write on `file.darta_link`. */}
      {session?.permissions.includes('file.darta_link') && file.archivedAt === null ? (
        <Button size="sm" variant="secondary" className="self-start" onClick={() => setActiveModal('darta')}>
          {t('fileDetail.darta.edit')}
        </Button>
      ) : null}
    </Card>
  );

  return (
    <div className="flex flex-col gap-4">
      {/*
        ux-review: "a back-link is not navigation." A dead-end "← Back to File Register"
        underneath the header told the user only where they could retreat TO, never where
        they currently WERE relative to the rest of the app. A breadcrumb states both in
        one line, and — because it is a location statement rather than an escape hatch —
        it does not need to be the only way out; the primary nav (`AppShell`) already
        covers that. `renderLink` hands the register crumb this app's own `react-router`
        `Link`, so following it stays a client-side navigation (Breadcrumb's own default
        `Link` is a plain `<a>`, which would force a full page reload here).
      */}
      <Breadcrumb
        items={[
          { label: t('shell.nav.files'), href: ROUTES.files },
          { label: file.trackingCode },
        ]}
        renderLink={(item) => (
          <Link to={item.href as string} className="text-text-link no-underline hover:underline">
            {item.label}
          </Link>
        )}
      />

      <Card className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="font-mono text-2xl font-semibold text-text-heading">{file.trackingCode}</h1>
          <StatusBadge entry={fileStageEntry(file.statusCode)} />
          {file.isOverdue ? <StatusBadge entry={OVERDUE_FLAG} variant="solid" /> : null}
          <Tag>{confidentialityLabel(file.confidentialityLevel, locale)}</Tag>
        </div>
        <p className="text-lg text-text-primary">
          {file.subject ?? <span className="italic text-text-muted">{t('fileRegister.subjectRedacted')}</span>}
        </p>
        <p className="text-sm text-text-muted">
          {t('fileDetail.registeredOn', { date: formatRegisteredDisplay(file.registeredDateBs, locale) })} ·{' '}
          {t('fileDetail.section', { name: locale === 'ne' ? file.section.nameNe : file.section.nameEn })}
          {' · '}
          {file.dueAt ? t('fileDetail.dueOn', { date: formatDueDisplay(file.dueAt, locale) }) : t('fileDetail.noDueDate')}
        </p>
      </Card>

      <Tabs
        label={file.trackingCode}
        items={[
          { value: 'overview', label: t('fileDetail.tabOverview'), content: overview },
          { value: 'history', label: t('fileDetail.tabHistory'), content: history },
          { value: 'attachments', label: t('fileDetail.tabAttachments'), content: attachments },
          { value: 'darta', label: t('fileDetail.tabDarta'), content: darta },
        ]}
      />

      {activeAction ? (
        <MovementActionModal
          open={activeAction !== null}
          onOpenChange={(open) => {
            if (!open) setActiveAction(null);
          }}
          fileId={fileId}
          action={activeAction}
          expectedVersion={file.version}
          expectedHolderUserId={file.currentHolderUserId}
          onRecorded={() => {
            invalidateFile();
            setCursors([undefined]);
            notifySuccess(`toast.movement.${activeAction}`);
          }}
          onStaleConflict={() => {
            invalidateFile();
            setCursors([undefined]);
          }}
        />
      ) : null}

      {activeModal === 'edit' ? (
        <EditFileModal
          open
          onOpenChange={(open) => !open && setActiveModal(null)}
          fileId={fileId}
          expectedVersion={file.version}
          currentSubject={file.subject ?? ''}
          currentDueAt={file.dueAt}
          onSuccess={() => {
            invalidateFile();
            notifySuccess('toast.file.edited');
          }}
          onStaleConflict={invalidateFile}
        />
      ) : null}

      {activeModal === 'darta' ? (
        <DartaChalaniModal
          open
          onOpenChange={(open) => !open && setActiveModal(null)}
          fileId={fileId}
          expectedVersion={file.version}
          currentDartaNumber={file.dartaChalani.dartaNumber}
          currentChalaniNumber={file.dartaChalani.chalaniNumber}
          onSuccess={() => {
            invalidateFile();
            notifySuccess('toast.file.darta');
          }}
          onStaleConflict={invalidateFile}
        />
      ) : null}

      {activeModal === 'classify' ? (
        <ClassifyModal
          open
          onOpenChange={(open) => !open && setActiveModal(null)}
          fileId={fileId}
          expectedVersion={file.version}
          currentLevel={file.confidentialityLevel}
          onSuccess={() => {
            invalidateFile();
            notifySuccess('toast.file.classified');
          }}
          onStaleConflict={invalidateFile}
        />
      ) : null}

      {activeModal === 'reopen' ? (
        <ReopenModal
          open
          onOpenChange={(open) => !open && setActiveModal(null)}
          fileId={fileId}
          expectedVersion={file.version}
          onSuccess={() => {
            invalidateFile();
            notifySuccess('toast.file.reopened');
          }}
          onStaleConflict={invalidateFile}
        />
      ) : null}

      {activeModal === 'reassign' ? (
        <ReassignModal
          open
          onOpenChange={(open) => !open && setActiveModal(null)}
          fileId={fileId}
          expectedVersion={file.version}
          onSuccess={() => {
            invalidateFile();
            notifySuccess('toast.file.reassigned');
          }}
          onStaleConflict={invalidateFile}
        />
      ) : null}

      {activeModal === 'qr' ? (
        <Modal
          open
          onOpenChange={(open) => !open && setActiveModal(null)}
          title={t('fileDetail.qr.title')}
          description={t('fileDetail.qr.body')}
          cancelLabel={t('fileDetail.preview.close')}
        >
          {qrQuery.isLoading ? (
            <Skeleton shape="list-row" count={2} />
          ) : qrQuery.isError ? (
            <AlertBanner
              variant="critical"
              title={t('fileDetail.qr.error')}
              action={
                <Button size="sm" variant="secondary" onClick={() => qrQuery.refetch()}>
                  {t('common.retry')}
                </Button>
              }
            />
          ) : qrQuery.data ? (
            <div data-print-area className="flex flex-col gap-4">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                <QrCode
                  value={trackingQrUrl(qrQuery.data.qrFragmentPath)}
                  label={t('fileIntake.success.qrLabel', { code: qrQuery.data.trackingCode })}
                  className="shrink-0"
                />
                <dl className="flex flex-col gap-2">
                  <div>
                    <dt className="text-sm text-text-muted">{t('fileDetail.qr.trackingCode')}</dt>
                    <dd className="font-mono text-lg text-text-heading">{qrQuery.data.trackingCode}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-text-muted">{t('fileDetail.qr.token')}</dt>
                    <dd className="break-all font-mono text-sm text-text-primary">{qrQuery.data.qrToken}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-text-muted">{t('fileDetail.qr.fragmentPath')}</dt>
                    <dd className="break-all font-mono text-xs text-text-muted">{qrQuery.data.qrFragmentPath}</dd>
                  </div>
                </dl>
              </div>
              <Button variant="secondary" size="sm" className="self-start" onClick={() => window.print()}>
                {t('fileIntake.success.print')}
              </Button>
            </div>
          ) : null}
        </Modal>
      ) : null}

      {previewAttachmentId ? (
        (() => {
          const attachment = (attachmentsQuery.data ?? []).find((a) => a.id === previewAttachmentId);
          if (!attachment) return null;
          return (
            <AttachmentPreviewModal
              attachment={attachment}
              onOpenChange={(open) => {
                if (!open) setPreviewAttachmentId(null);
              }}
            />
          );
        })()
      ) : null}

      {archivingId ? (
        <Modal
          open
          onOpenChange={(open) => !open && !archiveMutation.isPending && setArchivingId(null)}
          title={t('fileDetail.archive.action')}
          description={t('fileDetail.archive.confirm')}
          confirmLabel={t('fileDetail.archive.action')}
          cancelLabel={t('movement.cancel')}
          variant="destructive"
          loading={archiveMutation.isPending}
          onConfirm={() => archiveMutation.mutate(archivingId)}
        >
          {archiveMutation.isError ? <AlertBanner variant="critical" title={t('fileDetail.archive.error')} /> : null}
        </Modal>
      ) : null}
    </div>
  );
}
